// @spec AI-STR-006
// Onboarding page for AI-guided module setup

import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  OnboardingLayout,
  useCompleteOnboarding,
  useCreateOnboarding,
  useOnboardingSession,
  useOnboardingSessions,
  useOnboardingStore,
  useUpdateOnboarding,
} from '@/features/ai/onboarding';
import type { OnboardingModule } from '@/features/ai/onboarding';
import { ApiClientError } from '@/types/api';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ai/onboarding/$module')({
  beforeLoad: ({ params }) => {
    if (params.module === 'ghg') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router uses thrown notFound responses.
      throw notFound();
    }
  },
  component: OnboardingPage,
  validateSearch: (search: Record<string, unknown>) => ({
    sessionId: search.sessionId as string | undefined,
  }),
});

function OnboardingPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { module } = Route.useParams();
  const { sessionId: urlSessionId } = Route.useSearch();

  // Validate module
  const validModules: OnboardingModule[] = ['ghg', 'esg', 'nis2'];
  const isValidModule = validModules.includes(module as OnboardingModule);

  // Store state
  const activeSessionId = useOnboardingStore.use.activeSessionId();
  const setActiveSession = useOnboardingStore.use.setActiveSession();
  const setCurrentModule = useOnboardingStore.use.setCurrentModule();
  const setLocalCurrentStep = useOnboardingStore.use.setLocalCurrentStep();
  const reset = useOnboardingStore.use.reset();

  // Check for existing in-progress session
  const { data: existingSessions, isLoading: isLoadingSessions } = useOnboardingSessions({
    module: module as OnboardingModule,
    status: 'in_progress',
    enabled: isValidModule && !urlSessionId,
  });
  const { data: completedSessions, isLoading: isLoadingCompletedSessions } = useOnboardingSessions({
    module: module as OnboardingModule,
    status: 'completed',
    enabled: isValidModule && !urlSessionId,
  });

  // Fetch session data
  const sessionIdToUse = urlSessionId ?? existingSessions?.items[0]?.id ?? activeSessionId;
  const {
    data: session,
    isLoading: isLoadingSession,
    error: sessionError,
  } = useOnboardingSession(sessionIdToUse);

  // Mutations
  const { mutate: createSession, isPending: isCreating } = useCreateOnboarding({
    onSuccess: (newSession) => {
      setActiveSession(newSession.id);
      setCurrentModule(newSession.module);
      setLocalCurrentStep(newSession.currentStep);
      /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
      void navigate({
        to: '/ai/onboarding/$module',
        params: { module },
        search: { sessionId: newSession.id },
        replace: true,
      } as any);
      /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
    },
    onError: (error) => {
      if (error instanceof ApiClientError && error.status === 409) {
        const existingId = (error as ApiClientError & { existingSessionId?: string })
          .existingSessionId;
        if (existingId) {
          setActiveSession(existingId);
          /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
          void navigate({
            to: '/ai/onboarding/$module',
            params: { module },
            search: { sessionId: existingId },
            replace: true,
          } as any);
          /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
          return;
        }
      }
      toast.error(i18n.t('common:toast.failedToStartOnboarding'), { description: error.message });
    },
  });

  const { mutate: updateSession, isPending: isUpdating } = useUpdateOnboarding({
    onError: (error) => {
      toast.error(i18n.t('common:toast.failedToSaveProgress'), { description: error.message });
    },
  });

  const { mutate: completeSession, isPending: isCompleting } = useCompleteOnboarding({
    onSuccess: (response) => {
      toast.success(i18n.t('common:toast.onboardingCompleted'));
      reset();
      void navigate({ to: response.redirectUrl });
    },
    onError: (error) => {
      const missingFields = (error as ApiClientError & { missingFields?: string[] }).missingFields;
      if (missingFields && missingFields.length > 0) {
        toast.error(i18n.t('common:toast.cannotCompleteOnboarding'), {
          description: `Missing required fields: ${missingFields.join(', ')}`,
        });
      } else {
        toast.error(i18n.t('common:toast.failedToCompleteOnboarding'), {
          description: error.message,
        });
      }
    },
  });

  // Initialize on mount
  useEffect(() => {
    if (isValidModule) {
      setCurrentModule(module as OnboardingModule);
    }
    return () => {
      // Don't reset on unmount to preserve state during navigation
    };
  }, [module, isValidModule, setCurrentModule]);

  // Auto-redirect to existing session
  useEffect(() => {
    if (!urlSessionId && existingSessions?.items[0]) {
      const existingSession = existingSessions.items[0];
      setActiveSession(existingSession.id);
      /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
      void navigate({
        to: '/ai/onboarding/$module',
        params: { module },
        search: { sessionId: existingSession.id },
        replace: true,
      } as any);
      /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
    }
  }, [existingSessions, urlSessionId, module, navigate, setActiveSession]);

  // Sync session data to store
  useEffect(() => {
    if (session) {
      setActiveSession(session.id);
      setLocalCurrentStep(session.currentStep);
    }
  }, [session, setActiveSession, setLocalCurrentStep]);

  const handleStartOnboarding = useCallback(() => {
    reset();
    createSession({ module: module as OnboardingModule });
  }, [createSession, module, reset]);

  const localFormData = useOnboardingStore.use.localFormData();
  const latestCompletedSession = completedSessions?.items[0];
  const shouldShowCompletedState =
    (!session && !sessionIdToUse && Boolean(latestCompletedSession)) ||
    session?.status === 'completed';

  const handleStepChange = useCallback(
    (step: number) => {
      if (!session) return;
      setLocalCurrentStep(step);
      // Send both step change and current form data in a single PATCH to avoid
      // race conditions where a stale refetch between two separate PATCHes
      // overwrites local form state.
      updateSession({
        sessionId: session.id,
        data: { currentStep: step, formData: localFormData },
      });
    },
    [session, setLocalCurrentStep, updateSession, localFormData]
  );

  const handleFormDataChange = useCallback(
    (formData: Record<string, unknown>) => {
      if (!session) return;
      updateSession({
        sessionId: session.id,
        data: { formData },
      });
    },
    [session, updateSession]
  );

  const handleComplete = useCallback(() => {
    if (!session) return;
    completeSession({ sessionId: session.id, formData: localFormData });
  }, [session, completeSession, localFormData]);

  // Invalid module
  if (!isValidModule) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Invalid Module</AlertTitle>
          <AlertDescription>
            The module &quot;{module}&quot; is not valid. Please select from: GHG, ESG, or NIS2.
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => {
            void navigate({ to: '/dashboard' });
          }}
        >
          Go to Dashboard
        </Button>
      </div>
    );
  }

  // Loading state
  if (isLoadingSessions || isLoadingCompletedSessions || (sessionIdToUse && isLoadingSession)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common:routeErrors.loadingOnboarding')}</p>
        </div>
      </div>
    );
  }

  if (shouldShowCompletedState) {
    const moduleLabels: Record<OnboardingModule, string> = {
      ghg: 'GHG Emissions Reporting',
      esg: 'ESG / CSRD Reporting',
      nis2: 'NIS2 Classification & Scope',
    };
    const dashboardRoutes: Record<OnboardingModule, string> = {
      ghg: '/ghg/dashboard',
      esg: '/esg/dashboard',
      nis2: '/nis2/dashboard',
    };
    const completedAt = session?.completedAt ?? latestCompletedSession?.lastActivityAt;
    const moduleKey = module as OnboardingModule;

    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="max-w-md text-center space-y-6">
          <h1 className="text-2xl font-bold">Onboarding completed!</h1>
          <p className="text-muted-foreground">
            {moduleLabels[moduleKey]} has already been configured for this organization.
          </p>
          {completedAt ? (
            <p className="text-sm text-muted-foreground">
              Completed on {new Date(completedAt).toLocaleString()}
            </p>
          ) : null}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              onClick={() => {
                void navigate({ to: dashboardRoutes[moduleKey] });
              }}
            >
              View Dashboard
            </Button>
            <Button variant="outline" onClick={handleStartOnboarding} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start New Session'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No session - show start screen
  if (!session && !sessionIdToUse) {
    const moduleNames: Record<OnboardingModule, string> = {
      ghg: 'GHG Emissions Reporting',
      esg: 'ESG / CSRD Reporting',
      nis2: 'NIS2 Classification & Scope',
    };
    const moduleDurations: Record<OnboardingModule, string> = {
      ghg: '15-30 minutes',
      esg: '15-30 minutes',
      nis2: '5-10 minutes',
    };

    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="max-w-md text-center space-y-6">
          <h1 className="text-2xl font-bold">{moduleNames[module as OnboardingModule]} Setup</h1>
          <p className="text-muted-foreground">
            Leon will guide you through setting up your organization for{' '}
            {moduleNames[module as OnboardingModule].toLowerCase()}. This typically takes{' '}
            {moduleDurations[module as OnboardingModule]}.
          </p>
          <Button size="lg" onClick={handleStartOnboarding} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              'Start Setup'
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Session error
  if (sessionError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('common:routeErrors.errorLoadingSession')}</AlertTitle>
          <AlertDescription>
            {sessionError instanceof Error
              ? sessionError.message
              : t('common:routeErrors.failedToLoadOnboardingSession')}
          </AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-4" onClick={handleStartOnboarding}>
          Start New Session
        </Button>
      </div>
    );
  }

  // Session loaded - show onboarding layout
  if (!session) {
    return null;
  }

  return (
    <div className="h-[calc(100vh-4rem)]">
      <OnboardingLayout
        module={session.module}
        sessionId={session.id}
        conversationId={session.conversationId ?? ''}
        currentStep={session.currentStep}
        totalSteps={session.totalSteps}
        formData={session.formData}
        onStepChange={handleStepChange}
        onFormDataChange={handleFormDataChange}
        onComplete={handleComplete}
        isLoading={isUpdating || isCompleting}
      />
    </div>
  );
}
